// @vendors
import React, { useContext, useMemo, useState } from 'react'
import sx from "mui-sx";
import { useHistory } from 'react-router-dom';

// @components material
import {
    Box,
    Collapse,
    EditIcon,
    ExpandMoreIcon,
    Grid,
    IconButton,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    Tooltip,
} from '../../../components/shared/MaterialUI';
import EditNoteIcon from '@mui/icons-material/EditNote';

// @assets
import { UserManagementStyle } from '../../../assets/css/employeeTable-style';
import { GeneralStyles } from '../../../assets/css/general-style';

// @components
import TicketDetail from './TicketDetail';
import ResponseDialog from './ResponseDialog';

// @constants
import { ROUTENAME } from '../../../navigation/RouteName';

// @helpers
import { convertDateTolocaleTime, listStatusTickets, MenuItemProps } from '../../../helpers/FunctionsUtils';
import AppHelper from '../../../helpers/AppHelper';

// @service
import TicketService from '../../../services/newApi/TicketServiceNewApi';

// @store
import { useUI } from '../../../app/context/ui';
import { TicketContext } from '../TicketManagement';
import { useSelector } from 'react-redux';
import { theme } from '../../../assets/theme';

const TicketRow = ({row}) => {
    const generalStyle = useMemo(() => GeneralStyles(), []);
    const style = useMemo(() => UserManagementStyle(), []);
    const [open, setOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const history = useHistory();
    const { blockUI, snackbarUI } = useUI();
    const ticketService = useMemo(() => new TicketService(), []);
    const {handleMapResponse} = useContext(TicketContext);
    const hasDevPanelAccess = useSelector((state) => state?.user?.hasDevPanelAccess);

    const handleOpen = async () => {setOpen(!open);}

    const bgColorChip = () => {
        if(row?.statusId === 1)return `1px solid ${theme.palette.error.main}`;
        if(row?.statusId === 3)return `1px solid ${theme.palette.warning.main}`;
        return `1px solid ${theme.palette.success.main}`;
    }

    const handleClickOpen = () => setOpenModal(!openModal);

    const handleChange = async (e) => {
        const value = e.target.value;
        try {
            blockUI.current?.open(true);
            await ticketService.updateStatus(row.id, value);
            handleMapResponse();
        } catch (error) {
            AppHelper.checkError(error, snackbarUI);
            blockUI.current?.open(false);
        }
    }

  return (
    <>
        <TableRow>
            <TableCell
                scope="row"
                sx={sx(
                    generalStyle.tableCell,
                    { condition: open, sx: generalStyle.borderNone },
                    { condition: !open, sx: generalStyle.bottomBorder }
                )}
            >
                <IconButton onClick={handleOpen}>
                    <ExpandMoreIcon sx={{color: theme.palette.primary.black}} />
                </IconButton>
            </TableCell>

            <TableCell
                scope="row"
                sx={{...generalStyle.tableCell, textAlign: 'left', paddingLeft: '10px'}}
            >
                {row?.order}
            </TableCell>

            <TableCell
                scope="row"
                sx={{...generalStyle.tableCell, textAlign: 'left', paddingLeft: '10px'}}
            >
                {row?.name}
            </TableCell>
            
            <TableCell
                scope="row"
                sx={{
                    ...generalStyle.tableCell,
                    ...generalStyle.tableColumnHide,
                    textAlign: 'left'
                }}
            >
                {convertDateTolocaleTime(row?.createdAt)}
            </TableCell>
            
            <TableCell
                scope="row"
                sx={{
                    ...generalStyle.tableCell,
                    ...generalStyle.tableColumnHide,
                    textAlign: 'left'
                }}
            >
                {convertDateTolocaleTime(row?.updatedAt)}
            </TableCell>

            <TableCell
                scope="row"
                sx={{
                    ...generalStyle.tableCell,
                    ...generalStyle.tableColumnHide,
                    textAlign: 'left',
                }}
            >
                {row?.priority}
            </TableCell>
            
            <TableCell
                scope="row"
                sx={{...generalStyle.tableCell, textAlign: 'left'}}
            >
                <Box>
                    <Select
                        id="priority"
                        name='priority'
                        value={row?.statusId}
                        sx={{
                            width: '130px',
                            height: '40px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: bgColorChip()
                            },
                        }}
                        onChange={(e) => {handleChange(e);}}
                        MenuProps={MenuItemProps}
                    >
                        {listStatusTickets?.map((item, index) => (
                            <MenuItem
                                value={item.id}
                                key={index}
                            >
                                {item.value}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </TableCell>

            <TableCell
                sx={{
                    ...generalStyle.tableCell,
                    ...generalStyle.tableColumnHide
                }}
            >
            <Grid sx={style.containerButtons}>
                {hasDevPanelAccess &&
                    <Tooltip
                        title={<p style={{ fontSize: "16px", margin: "0" }}>Response from devs</p>}
                        arrow
                    >
                        <IconButton
                            onClick={handleClickOpen}
                            sx={style.btnEdit}
                        >
                            <EditNoteIcon sx={{fontSize: "1.8rem"}} />
                        </IconButton>
                    </Tooltip>
                }
                <Tooltip
                    title={<p style={{ fontSize: "16px", margin: "0" }}>Edit ticket</p>}
                    arrow
                >
                    <IconButton
                        onClick={() => history.push(`${ROUTENAME.editTicket}/${row?.id}`, {id: row.id})}
                        sx={style.btnEdit}
                    >
                        <EditIcon sx={{fontSize: "1.8rem"}} />
                    </IconButton>
                </Tooltip>
            </Grid>
            </TableCell>      
        </TableRow>

        <TableRow>
            <TableCell
                sx={sx(
                    generalStyle.tableCell,
                    { padding: "0"},
                    { condition: !open, sx: generalStyle.borderNone },
                    { condition: open, sx: generalStyle.bottomBorder }
                )}
                colSpan={12}
            >
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    sx={{ width: '100%' }}
                >
                    <TicketDetail row={row} />
                </Collapse>
            </TableCell>
        </TableRow>
        <ResponseDialog
            open={openModal}
            handleCloseModal={handleClickOpen}
            id={row.id}
        />
    </>
  )
}

export default TicketRow